'use client'
import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { useState } from "react";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";

/**
 * @typedef {import("@prismicio/client").Content.ContactFormLayoutSlice} ContactFormLayoutSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ContactFormLayoutSlice>} ContactFormLayoutProps
 * @type {import("react").FC<ContactFormLayoutProps>}
 */
const ContactFormLayout = ({ slice, context, index }) => {
  const { title, text, rgpd } = slice.primary;
  
  // États pour le formulaire
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLoading) return;
    
    setIsLoading(true);
    setMessage('');

    try {
      const formData = new FormData(e.target);
      
      const response = await fetch('/api/contact', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        // Réinitialiser le formulaire
        e.target.reset();
      } else {
        setMessage(data.error || 'Une erreur est survenue');
      }
    } catch (error) {
      setMessage('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={` ${index == 0 ? 'mb-20 md:mb-30' : 'mt-16 mb-20 md:mt-23 md:mb-30 '} bg-white framing text-dark-green`}
    >
      <div className="max-w-375 m-auto">
        <Breadcrumbs context={context} index={index} textcolor={"text-dark-green"} />
        <div className="flex flex-col min-[1120px]:flex-row gap-8 md:gap-15 justify-between">
          <div className="flex flex-col gap-5">
            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="h3">{children}</h1>
                  ),
                  heading2: ({ children }) => (
                    <h2 className="h3">{children}</h2>
                  ),
                  heading3: ({ children }) => (
                    <h3 className="h3">{children}</h3>
                  ),
                }}
              />
            )}
            <div className="flex flex-col gap-2">
              {isFilled.richText(text) && (
                <PrismicRichText
                  field={text}
                  components={{
                    paragraph: ({ children }) => (
                      <p>{children}</p>
                    ),
                  }}
                />
              )}
            </div>
          </div>
          <div className="contact-form min-[1120px]:max-w-160">
            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4 bg-light-gray p-7 rounded-[16px]">
              <input type="text" name="_hp" tabIndex={-1} autoComplete="off" hidden />
              <div className="flex flex-col md:flex-row gap-4">
                {/* Prénom */}
                <div className="w-full md:w-1/2 flex flex-col">
                  <label htmlFor="firstName">Prénom</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    placeholder="Christelle"
                    autoComplete="given-name"
                    disabled={isLoading}
                  />
                </div>

                {/* Nom */}
                <div className="w-full md:w-1/2 flex flex-col">
                  <label htmlFor="lastName">Nom</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    placeholder="Dupont"
                    autoComplete="family-name"
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                {/* Téléphone */}
                <div className="w-full md:w-1/2 flex flex-col">
                  <label htmlFor="phone">Téléphone</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    placeholder="06 11 11 11 11"
                    autoComplete="tel"
                    pattern="^(\+?\\d{1,3}[\\s.]?)?0?\\d([\\s.]?\\d{2}){4}$"
                    disabled={isLoading}
                  />
                </div>

                {/* Vous êtes */}
                <div className="w-full md:w-1/2 flex flex-col">
                  <label htmlFor="audience">Vous êtes</label>
                  <select id="audience" name="audience" required defaultValue="" disabled={isLoading}>
                    <option value="particulier">Un particulier</option>
                    <option value="collectivite">Une collectivité</option>
                    <option value="club">Un club</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              {/* Email */}
              <div className="w-full flex flex-col">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="exemple@yyp.com"
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>

              <div className="flex flex-col md:flex-row gap-4">
                {/* Commune */}
                <div className="w-full md:w-1/2 flex flex-col">
                  <label htmlFor="city">Commune</label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    required
                    placeholder="Marseille"
                    autoComplete="address-level2"
                    disabled={isLoading}
                  />
                </div>

                {/* Code Postal */}
                <div className="w-full md:w-1/2 flex flex-col">
                  <label htmlFor="postalCode">Code Postal</label>
                  <input
                    id="postalCode"
                    name="postalCode"
                    type="text"
                    inputMode="numeric"
                    placeholder="13008"
                    autoComplete="postal-code"
                    pattern="^\\d{5}$"
                    maxLength={5}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Message */}
              <div className="w-full flex flex-col">
                <label htmlFor="message">Votre message</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  placeholder="Je souhaite que Yes Yes Padel s'installe dans ma ville, je travaille actuellement pour le tennis club…"
                  disabled={isLoading}
                />
              </div>

              {/* Consentement */}
              <div>
                <input id="consent" name="consent" type="checkbox" required disabled={isLoading} />
                <label htmlFor="consent">
                  {isFilled.richText(rgpd) && (
                    <PrismicRichText
                      field={rgpd}
                      components={{
                        paragraph: ({ children }) => (
                          <span>{children}</span>
                        ),
                      }}
                    />
                  )}
                </label>
              </div>

              {/* Bouton */}
              <div>
                <button type="submit" aria-label="Valider l'envoi du formulaire" className="w-full md:w-auto min-w-45 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading}>
                  {isLoading ? 'EN COURS...' : 'VALIDER'}
                </button>
              </div>

              {/* Métadonnées */}
              <input type="hidden" name="_source" value="contact-form" />
            </form>
            
            {message && (
              <div className={`mt-4 p-3 w-full rounded-lg text-sm ${message.includes('succès') || message.includes('envoyé') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormLayout;
